import React, { useState } from 'react';
import { fetchSettings, createNewInstance, checkForUpdate } from '../../api';

const APP_VERSION = require('../../../package.json').version;

function GeneralSection({ settings, setSettings, setMessage }) {
  const [newInstanceResult, setNewInstanceResult] = useState(null);
  const [updateCheck, setUpdateCheck] = useState(null);

  const handleNewInstance = async () => {
    if (!window.confirm('Neue Instanz erstellen?\n\nDas aktuelle Verzeichnis bleibt vollständig erhalten. Es wird ein neues, leeres Verzeichnis angelegt.')) return;
    setMessage(null);
    try {
      const result = await createNewInstance();
      setNewInstanceResult(result);
      setMessage({ type: 'success', text: result.message });
    } catch (err) {
      setMessage({ type: 'error', text: 'Fehler: ' + err.message });
    }
  };

  return (
    <>
      {/* Suche nach Updates */}
      <section className="settings-section">
        <h3 className="settings-section-title">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="23 4 23 10 17 10"/><path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/>
          </svg>
          Updates
        </h3>
        <p className="settings-desc">
          Aktuelle Version: <strong>v{APP_VERSION}</strong>
        </p>
        <button className="settings-btn-secondary" disabled={updateCheck === 'checking'} onClick={async () => {
          setUpdateCheck('checking');
          const info = await checkForUpdate(APP_VERSION);
          if (info) {
            setUpdateCheck(info);
            localStorage.removeItem('updateDismissed');
          } else {
            setUpdateCheck('uptodate');
          }
        }}>
          {updateCheck === 'checking' ? 'Prüfe…' : 'Suche nach Updates'}
        </button>
        {updateCheck && updateCheck !== 'checking' && updateCheck !== 'uptodate' && (
          <div className="settings-update-result">
            <p><strong>Luhm v{updateCheck.latest}</strong> ist verfügbar{updateCheck.notes ? ` — ${updateCheck.notes}` : ''}</p>
            <a href={updateCheck.url || 'https://luhm.app/download'} target="_blank" rel="noopener noreferrer" className="settings-btn-primary" style={{ display: 'inline-block', marginTop: '8px', textDecoration: 'none', textAlign: 'center' }}>
              Herunterladen
            </a>
          </div>
        )}
        {updateCheck === 'uptodate' && (
          <p className="settings-desc" style={{ marginTop: '8px', color: 'var(--color-text-secondary)' }}>
            Du verwendest die neueste Version.
          </p>
        )}
      </section>

      {/* Datenordner wechseln — nur in der Electron-App sichtbar */}
      {window.electronAPI && (
        <section className="settings-section settings-section-divider">
          <h3 className="settings-section-title">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"/>
              <line x1="12" y1="11" x2="12" y2="17"/><line x1="9" y1="14" x2="15" y2="14"/>
            </svg>
            Datenordner wechseln
          </h3>
          <p className="settings-desc">
            Wählt einen anderen Ordner als Datenbasis. Alle Zettel, Readwise-Highlights und Einstellungen werden aus dem neuen Ordner geladen. Der aktuelle Ordner bleibt unverändert.
          </p>
          <button className="settings-btn-secondary" onClick={async () => {
            const result = await window.electronAPI.changeDataFolder();
            if (result?.success) {
              setMessage({ type: 'success', text: `Ordner gewechselt: ${result.path}` });
              setTimeout(() => fetchSettings().then(setSettings).catch(() => {}), 1000);
            }
          }}>
            Ordner auswählen…
          </button>
        </section>
      )}

      {/* Daten exportieren */}
      <section className="settings-section settings-section-divider">
        <h3 className="settings-section-title">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/>
          </svg>
          Daten exportieren
        </h3>
        <p className="settings-desc">
          Lädt alle Zettel, Readwise-Highlights und Anhänge als ZIP-Datei herunter. Deine Daten gehören dir — kein Lock-in, keine Abhängigkeit.
        </p>
        <button className="settings-btn-secondary" onClick={() => {
          const a = document.createElement('a');
          a.href = '/api/export';
          a.download = 'luhm-export.zip';
          a.click();
        }}>
          Alle Daten als ZIP herunterladen
        </button>
      </section>

      {/* Neue Instanz */}
      <section className="settings-section settings-section-divider">
        <h3 className="settings-section-title">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="1 4 1 10 7 10"/><path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10"/>
          </svg>
          Neue Instanz
        </h3>
        <p className="settings-desc">
          Erstellt ein neues, leeres Arbeitsverzeichnis mit frischer Konfiguration. Das bisherige Verzeichnis bleibt vollständig erhalten — nichts wird gelöscht. Ideal zum Testen oder für einen sauberen Neustart.
        </p>
        <button className="settings-btn-secondary" onClick={handleNewInstance}>
          Neue Instanz erstellen
        </button>
        {newInstanceResult && (
          <div className="settings-new-instance-result">
            <div className="settings-path-item">
              <span className="settings-path-label">Neues Verzeichnis</span>
              <code className="settings-path-value">{newInstanceResult.new_path}</code>
            </div>
            <p className="settings-hint">{newInstanceResult.hint}</p>
          </div>
        )}
      </section>
    </>
  );
}

export default GeneralSection;
